import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { CloseCircleOutlined } from "@ant-design/icons";
import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import { Button, Col, Form, Input, Row, Select } from "antd";
import type { PurchaseContractFormValues } from "../type";
import styles from "@/asserts/styles/formSection.module.less";
import { factoryOptions, PurchaseRequestType } from "@/constants";
import { useUserSearch } from "@/hooks/behaviors/useUserSearch";
import { getDeptAndManagerByApplicant } from "@/apis/dept.api";
import { useFormEditable } from "../hooks/useFormEditable";
import { useModalBehavior } from "@/hooks/behaviors";
import { PurchaseRequestPageSelectorModal } from "@/components/selectorModal/PurchaseRequestPageSelectorModal/PurchaseRequestPageSelectorModal";
import { SrmPurchaseRequestVo } from "@/types/apis";
import { useModalStore } from "@/stores/modalStore";
import { usePurchaseRequestDetail } from "@/pages/requirementManagement/purchaseRequestManagement/hooks/usePurchaseRequest";
import { useTranslation } from "react-i18next";

const CONTRACT_SOURCE_OPTIONS = [
  { label: "供应商提供", value: "S1" },
  { label: "BF合同模版", value: "S2" },
  {
    label: "常规服务年度合同更新(与同一供应商续签)",
    value: "S3",
  },
  {
    label: "不能提供合同但有采购订单(必须说明理由)",
    value: "S0",
  },
];

/**
 * 申请人信息
 */
export function ApplicantFormFields() {
  const { t } = useTranslation("contractManagement");
  const form = Form.useFormInstance();

  const applicantNickname = Form.useWatch("applicantNickname", form);

  const { handleSearch: handleSearchUser, userIdOptions: userOptions } =
    useUserSearch(applicantNickname);

  const {
    open: purchaseRequestPageModalOpen,
    openModal: openPurchaseRequestPageModal,
    closeModal: closePurchaseRequestPageModal,
  } = useModalBehavior();

  const { isMainFormDisabled } = useFormEditable();

  const onPurchaseRequestPageModalOk = (
    checkedRows: SrmPurchaseRequestVo[],
  ) => {
    if (checkedRows.length === 0) {
      return;
    }

    form.setFieldsValue({
      approvedPurchaseApplyId: checkedRows[0].id,
      approvedPurchaseApplyNo: checkedRows[0].applicationNo,
    });
  };

  return (
    <div className={styles.formSection}>
      <ModalSectionHeader title={t("申请人信息")} />

      <div className={styles.sectionBody}>
        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="applicantId"
              label={t("申请人")}
              rules={[
                {
                  required: true,
                  message: t("请选择申请人"),
                },
              ]}
            >
              <Select
                placeholder={t("请选择")}
                showSearch
                filterOption={false}
                onSearch={handleSearchUser}
                options={userOptions}
                onSelect={async (value, option) => {
                  // 当选择用户时，同时更新隐藏字段
                  form.setFieldsValue({
                    applicantName: option?.raw.userName,
                    applicantNickname: option?.raw.nickName,
                  });
                  // 根据申请人获取部门及部门主管
                  const res = await getDeptAndManagerByApplicant(option.raw.id);
                  if (res.data) {
                    form.setFieldsValue({
                      departmentName: res.data.data.deptName,
                      departmentManagerName: res.data.data.managerNickName,
                    });
                  }
                }}
              />
            </Form.Item>
            <Form.Item hidden name="applicantName">
              <Input />
            </Form.Item>
            <Form.Item hidden name="applicantNickname">
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="departmentName"
              label={t("部门")}
            >
              <Input placeholder={t("自动带出")} disabled />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="departmentManagerName"
              label={t("部门主管")}
            >
              <Input placeholder={t("自动带出")} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="factory"
              label={t("工厂")}
              rules={[{ required: true, message: t("请选择工厂") }]}
            >
              <Select placeholder={t("请选择")} options={factoryOptions} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="contractSource"
              label={t("合同来源")}
              rules={[
                {
                  required: true,
                  message: t("请选择合同来源"),
                },
              ]}
            >
              <Select
                placeholder={t("请选择")}
                options={CONTRACT_SOURCE_OPTIONS}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              label={t("已批准采购申请表编号")}
            >
              <Form.Item name="approvedPurchaseApplyNo" hidden />
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.approvedPurchaseApplyNo !==
                  currentValues.approvedPurchaseApplyNo
                }
              >
                {({ getFieldValue, setFieldsValue }) => {
                  const valueNo = getFieldValue("approvedPurchaseApplyNo");
                  const valueId = getFieldValue("approvedPurchaseApplyId");

                  const handleRemove = () => {
                    setFieldsValue({
                      approvedPurchaseApplyNo: undefined,
                      approvedPurchaseApplyId: undefined,
                    });
                  };

                  return valueNo ? (
                    <PurchaseApplyNoDisplay
                      canRemove={!isMainFormDisabled}
                      valueNo={valueNo}
                      valueId={valueId}
                      onRemove={handleRemove}
                    />
                  ) : (
                    <Input
                      placeholder={t("请选择")}
                      readOnly
                      onClick={openPurchaseRequestPageModal}
                    />
                  );
                }}
              </Form.Item>
            </Form.Item>
            <Form.Item<PurchaseContractFormValues>
              hidden
              name="approvedPurchaseApplyId"
            ></Form.Item>
            <PurchaseRequestPageSelectorModal
              selectionMode="single"
              open={purchaseRequestPageModalOpen}
              onOk={onPurchaseRequestPageModalOk}
              onClose={closePurchaseRequestPageModal}
            />
          </Col>

          <Col span={24}>
            <MaterialFileFormFields
              fieldName="attachments"
              label={t("附件上传")}
              required={false}
              bindFlow={true}
              hiddenRemove={isMainFormDisabled}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
}

interface PurchaseApplyNoDisplayProps {
  valueId: number;
  valueNo: string;
  canRemove: boolean;
  onRemove: () => void;
}

function PurchaseApplyNoDisplay({
  valueId,
  valueNo,
  canRemove,
  onRemove,
}: PurchaseApplyNoDisplayProps) {
  const { openModal } = useModalStore();

  const detail = usePurchaseRequestDetail(valueId);

  const handleView = () => {
    openModal("PURCHASE_REQUEST", {
      recordId: valueId,
      type: detail?.formType as PurchaseRequestType,
      mode: "started",
      isReadOnly: true,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Button disabled={false} type="link" onClick={handleView}>
        {valueNo}
      </Button>
      {canRemove && (
        <CloseCircleOutlined style={{ color: "red" }} onClick={onRemove} />
      )}
    </div>
  );
}
